import React from "react";
import { Container } from "react-bootstrap";
import "../../styles/LegalDocuments.css";
import SEO from "../utils/SEO";

const TermsOfService = () => {
  return (
    <div className="legal-document">
      <SEO
        title="Terms of Service - Sarv"
        description="Read Sarv's terms of use including subscription plans, cancellation policy, medical disclaimers, and user agreements."
        canonicalPath="/terms-of-service"
      />
      <Container className="legal-container">
        <div className="legal-header">
          <h1>SARV TERMS OF USE</h1>
          <p className="last-updated">Last Updated: June 5, 2026</p>
        </div>

        <div className="important-notice">
          <h3>IMPORTANT NOTICE</h3>
          <p>
            <strong>AUTO-RENEWAL:</strong> If you purchase a subscription, it
            will automatically renew at the end of your billing period unless
            you cancel before the renewal date.
          </p>
          <p>
            <strong>CALIFORNIA RESIDENTS:</strong> You may cancel within 3
            business days of purchase for a full refund. See Section 8.3 for
            details.
          </p>
          <p>
            <strong>ARBITRATION:</strong> Section 21 contains an arbitration
            agreement. You have 30 days to opt out. See Section 21.6 for
            details.
          </p>
        </div>

        <div className="toc">
          <h2>Table of Contents</h2>
          <div className="toc-grid">
            <div className="toc-item">
              <span className="toc-number">1.</span>
              <a href="#section1">Agreement to Terms</a>
            </div>
            <div className="toc-item">
              <span className="toc-number">2.</span>
              <a href="#section2">Our Service</a>
            </div>
            <div className="toc-item">
              <span className="toc-number">3.</span>
              <a href="#section3">Who Can Use Sarv</a>
            </div>
            <div className="toc-item">
              <span className="toc-number">4.</span>
              <a href="#section4">No Medical Advice</a>
            </div>
            <div className="toc-item">
              <span className="toc-number">5.</span>
              <a href="#section5">Registration & Account Security</a>
            </div>
            <div className="toc-item">
              <span className="toc-number">6.</span>
              <a href="#section6">Subscriptions & Auto-Renewal</a>
            </div>
            <div className="toc-item">
              <span className="toc-number">7.</span>
              <a href="#section7">Free Trials & Promotions</a>
            </div>
            <div className="toc-item">
              <span className="toc-number">8.</span>
              <a href="#section8">Cancellation & Refunds</a>
            </div>
            <div className="toc-item">
              <span className="toc-number">9.</span>
              <a href="#section9">Payment Methods</a>
            </div>
            <div className="toc-item">
              <span className="toc-number">10.</span>
              <a href="#section10">App Store Subscriptions</a>
            </div>
            <div className="toc-item">
              <span className="toc-number">11.</span>
              <a href="#section11">User Content</a>
            </div>
            <div className="toc-item">
              <span className="toc-number">12.</span>
              <a href="#section12">Prohibited Uses</a>
            </div>
            <div className="toc-item">
              <span className="toc-number">13.</span>
              <a href="#section13">Intellectual Property</a>
            </div>
            <div className="toc-item">
              <span className="toc-number">14.</span>
              <a href="#section14">Privacy & Data</a>
            </div>
            <div className="toc-item">
              <span className="toc-number">15.</span>
              <a href="#section15">Third-Party Services</a>
            </div>
            <div className="toc-item">
              <span className="toc-number">16.</span>
              <a href="#section16">Service Modifications</a>
            </div>
            <div className="toc-item">
              <span className="toc-number">17.</span>
              <a href="#section17">Termination</a>
            </div>
            <div className="toc-item">
              <span className="toc-number">18.</span>
              <a href="#section18">Disclaimers & Liability</a>
            </div>
            <div className="toc-item">
              <span className="toc-number">19.</span>
              <a href="#section19">Indemnification</a>
            </div>
            <div className="toc-item">
              <span className="toc-number">20.</span>
              <a href="#section20">Export Control</a>
            </div>
            <div className="toc-item">
              <span className="toc-number">21.</span>
              <a href="#section21">Arbitration Agreement</a>
            </div>
            <div className="toc-item">
              <span className="toc-number">22.</span>
              <a href="#section22">Class Action Waiver</a>
            </div>
            <div className="toc-item">
              <span className="toc-number">23.</span>
              <a href="#section23">Governing Law</a>
            </div>
            <div className="toc-item">
              <span className="toc-number">24.</span>
              <a href="#section24">General Terms</a>
            </div>
            <div className="toc-item">
              <span className="toc-number">25.</span>
              <a href="#section25">Contact Us</a>
            </div>
          </div>
        </div>

        {/* Section 1 */}
        <section id="section1">
          <h2>1. Agreement to Terms</h2>
          <p>
            Welcome to Sarv! These Terms of Use ("Terms") constitute a legally
            binding agreement between you and M31-V1 LLC ("Sarv," "we," "us," or
            "our") governing your access to and use of the Sarv platform,
            including our website, mobile applications, and all related services
            (collectively, the "Service").
          </p>

          <div className="highlight-box">
            <p>
              <strong>
                By creating an account, accessing, or using the Service, you
                agree to be bound by these Terms and all policies referenced
                herein, including our Privacy Policy. If you do not agree to
                these Terms, you may not use the Service.
              </strong>
            </p>
          </div>

          <p>
            We may revise these Terms from time to time. When we make material
            changes, we will notify you by email or through the Service. Your
            continued use after such notice constitutes acceptance of the
            revised Terms.
          </p>

          <p>
            <strong>
              You should print or save a copy of these Terms for your records.
            </strong>
          </p>
        </section>

        {/* Section 2 */}
        <section id="section2">
          <h2>2. Our Service and What It Provides</h2>

          <h3>2.1 What We Do</h3>
          <p>
            Sarv's mission is to help people achieve optimal health through
            holistic, natural, and integrative wellness approaches. Our Service
            provides:
          </p>
          <ul>
            <li>Personalized health and wellness recommendations</li>
            <li>
              Access to information about medicinal herbs, natural remedies, and
              holistic recipes
            </li>
            <li>Wellness tracking and progress monitoring</li>
            <li>
              Educational content about natural medicine and preventive health
            </li>
            <li>
              Community features for connecting with other wellness-minded
              individuals (where available)
            </li>
            <li>
              Optional access to holistic healthcare practitioners (where
              available)
            </li>
          </ul>

          <h3>2.2 What We Don't Do</h3>
          <div className="warning-box">
            <p>
              <strong>
                Sarv is NOT a medical service provider, and we do NOT provide
                medical advice, diagnosis, or treatment.
              </strong>{" "}
              The Service is designed for informational and educational purposes
              to support your wellness journey.
            </p>
          </div>

          <h3>2.3 Service Evolution</h3>
          <p>
            Our Service is constantly evolving. We may update, modify, suspend,
            or discontinue any aspect of the Service at any time, with or
            without notice.
          </p>

          <h3>2.4 Generative AI and Content Accuracy</h3>
          <p>
            The Service may use artificial intelligence to provide personalized
            recommendations. AI-generated content may sometimes be inaccurate.
            You should use discretion and not rely solely on AI-generated
            content for any decisions, especially those related to health.
          </p>
        </section>

        {/* Section 3 */}
        <section id="section3">
          <h2>3. Who Can Use Sarv</h2>

          <h3>3.1 Age Requirements</h3>
          <div className="highlight-box">
            <p>
              You must be <strong>at least 18 years old</strong> to create an
              account and use the Service. We do not knowingly collect
              information from anyone under 18 years of age.
            </p>
          </div>

          <h3>3.2 Eligibility and Capacity</h3>
          <p>By using the Service, you represent and warrant that:</p>
          <ul>
            <li>
              You have the legal capacity to enter into this binding agreement
            </li>
            <li>
              You are not prohibited from using the Service under applicable
              laws
            </li>
            <li>
              All information you provide is accurate, current, and complete
            </li>
            <li>You will maintain the accuracy of your information</li>
          </ul>

          <h3>3.3 Geographic Restrictions</h3>
          <p>
            The Service is intended for users in the United States. If you
            access the Service from outside the U.S., you do so at your own risk
            and are responsible for compliance with local laws.
          </p>
        </section>

        {/* Section 4 */}
        <section id="section4">
          <h2>4. No Medical Advice – Please Read Carefully</h2>

          <div className="warning-box critical">
            <h3>CRITICAL MEDICAL DISCLAIMER</h3>
            <p className="caps-text">
              <strong>
                THE SERVICE IS FOR INFORMATIONAL AND EDUCATIONAL PURPOSES ONLY.
                SARV DOES NOT PROVIDE MEDICAL ADVICE, DIAGNOSIS, OR TREATMENT.
                THE SERVICE IS NOT A SUBSTITUTE FOR PROFESSIONAL MEDICAL CARE.
              </strong>
            </p>
          </div>

          <h3>4.1 Consult Healthcare Professionals</h3>
          <p>
            <strong>
              You should always consult with a qualified healthcare provider
              before:
            </strong>
          </p>
          <ul>
            <li>Starting any new health or wellness program</li>
            <li>Taking any herbs, supplements, or natural remedies</li>
            <li>Making changes to your diet, exercise routine, or lifestyle</li>
            <li>Stopping or changing any prescribed medication or treatment</li>
            <li>If you have any medical conditions or health concerns</li>
          </ul>

          <h3>4.2 No Physician-Patient Relationship</h3>
          <p>
            <strong>
              No physician-patient, therapist-patient, or other healthcare
              professional relationship is created
            </strong>{" "}
            between you and Sarv by your use of the Service.
          </p>

          <h3>4.3 Emergency Situations</h3>
          <div className="warning-box critical">
            <p className="caps-text">
              <strong>
                IF YOU ARE EXPERIENCING A MEDICAL EMERGENCY, CALL 911 OR GO TO
                THE NEAREST EMERGENCY ROOM IMMEDIATELY. DO NOT USE THE SERVICE
                FOR EMERGENCIES.
              </strong>
            </p>
          </div>

          <h3>4.4 Individual Results May Vary</h3>
          <p>
            Health and wellness outcomes vary significantly from person to
            person. We make no guarantees about the results you may achieve
            using the Service.
          </p>

          <h3>4.5 User-Generated Content</h3>
          <p>
            Much of the content on the Service is created by users. We do not
            verify the accuracy of user-generated content and explicitly
            disclaim responsibility for it.
          </p>

          <h3>4.6 Herb and Supplement Safety</h3>
          <p>
            Herbs and natural remedies can have side effects and may interact
            with medications or medical conditions.{" "}
            <strong>
              You are solely responsible for verifying the safety and
              appropriateness of any herbs or supplements before use.
            </strong>
          </p>
        </section>

        {/* Section 5 */}
        <section id="section5">
          <h2>5. Registration and Account Security</h2>

          <h3>5.1 Account Creation</h3>
          <p>
            To access most features, you must register for an account. You agree
            to:
          </p>
          <ul>
            <li>Provide accurate, current, and complete information</li>
            <li>Maintain and promptly update your account information</li>
            <li>
              You may use a preferred name or nickname — your legal or full name
              is not required
            </li>
            <li>Not create an account on behalf of another person</li>
          </ul>

          <h3>5.2 Account Security</h3>
          <p>
            <strong>You are responsible for:</strong>
          </p>
          <ul>
            <li>Maintaining the confidentiality of your password</li>
            <li>All activities that occur under your account</li>
            <li>Notifying us immediately of any unauthorized access</li>
          </ul>
          <p>
            <strong>Do not share your account or password with anyone.</strong>
          </p>

          <h3>5.3 One Account Per Person</h3>
          <p>Each user may only maintain one active account.</p>

          <h3>5.4 Linked Third-Party Accounts</h3>
          <p>
            You may link your Sarv account with third-party services (for
            example, by signing in with Apple or Google). You are responsible
            for complying with the third party's terms.
          </p>
        </section>

        {/* Section 6 - Updated with new pricing */}
        <section id="section6">
          <h2>6. Subscriptions and Payments – Auto-Renewal</h2>

          <h3>6.1 Subscription Required</h3>
          <p>
            Access to most features requires a paid subscription. Different
            subscription tiers may offer different features.
          </p>

          <h3>6.2 Automatic Renewal</h3>
          <div className="warning-box">
            <p className="caps-text">
              <strong>
                YOUR SUBSCRIPTION WILL AUTOMATICALLY RENEW AT THE END OF EACH
                BILLING PERIOD (MONTHLY, ANNUALLY, OR AS SPECIFIED) UNLESS YOU
                CANCEL BEFORE THE RENEWAL DATE.
              </strong>
            </p>
            <ul>
              <li>
                <strong>Automatic Charges:</strong> You authorize us to charge
                your payment method automatically
              </li>
              <li>
                <strong>Current Pricing:</strong> You will be charged at the
                then-current subscription price
              </li>
              <li>
                <strong>No Refunds:</strong> We do not provide refunds for
                partial billing periods
              </li>
            </ul>
          </div>

          <h3>6.3 Subscription Fees</h3>
          <p>By subscribing, you agree to pay:</p>
          <ul>
            <li>The subscription fee for your chosen plan</li>
            <li>Any applicable taxes</li>
            <li>Any add-on services or features you purchase</li>
          </ul>

          <h3>6.4 Price Changes</h3>
          <p>
            We reserve the right to change subscription prices at any time. If
            we increase your price:
          </p>
          <ul>
            <li>We will notify you in advance</li>
            <li>The new price applies at your next renewal</li>
            <li>You may cancel before the renewal to avoid the new price</li>
          </ul>

          <h3>6.5 Failed Payments</h3>
          <p>
            If we cannot charge your payment method, we may suspend or terminate
            your account. You remain responsible for all unpaid amounts.
          </p>
        </section>

        {/* Section 7 - Updated with new trial policy */}
        <section id="section7">
          <h2>7. Free Trials and Promotional Offers</h2>

          <h3>7.1 Free Trials</h3>
          <div className="highlight-box">
            <p>
              <strong>Important:</strong> Your payment method will be charged
              automatically when the trial ends unless you cancel before the
              trial period expires.
            </p>
            <ul>
              <li>
                You must cancel at least 24 hours before the trial ends to avoid
                charges
              </li>
              <li>
                Free trials are limited to new subscribers (one per person)
              </li>
              <li>
                We do not send a separate notice when your trial is ending
              </li>
            </ul>
          </div>

          <h3>7.2 Promotional Pricing</h3>
          <p>
            Promotional terms will be disclosed at the time of the offer. Unless
            otherwise stated:
          </p>
          <ul>
            <li>Promotional pricing applies only for the initial term</li>
            <li>Your subscription will renew at the regular price</li>
            <li>Promotional offers cannot be combined</li>
          </ul>

          <h3>7.3 Eligibility</h3>
          <p>
            We determine eligibility for free trials and promotions in our sole
            discretion.
          </p>
        </section>

        {/* Section 8 - Updated refund policy */}
        <section id="section8">
          <h2>8. Cancellation and Refunds</h2>

          <h3>8.1 How to Cancel Your Subscription</h3>

          <h4>If you subscribed through our website:</h4>
          <ul>
            <li>Log into your account at joinsarv.com</li>
            <li>Go to Account Settings → Subscriptions</li>
            <li>Follow the cancellation instructions</li>
          </ul>

          <h4>If you subscribed through Apple App Store:</h4>
          <ul>
            <li>Open Settings on your iOS device</li>
            <li>Tap your name → Subscriptions</li>
            <li>Select Sarv → Cancel Subscription</li>
            <li>
              Or visit:{" "}
              <a
                href="https://support.apple.com/en-us/HT202039"
                target="_blank"
                rel="noopener noreferrer"
              >
                Apple Support
              </a>
            </li>
          </ul>

          <h4>If you subscribed through Google Play:</h4>
          <ul>
            <li>Open Google Play Store on your Android device</li>
            <li>Tap Menu → Subscriptions</li>
            <li>Select Sarv → Cancel Subscription</li>
            <li>
              Or visit:{" "}
              <a
                href="https://support.google.com/googleplay/answer/7018481"
                target="_blank"
                rel="noopener noreferrer"
              >
                Google Support
              </a>
            </li>
          </ul>

          <h3>8.2 When Cancellation Takes Effect</h3>
          <ul>
            <li>
              Cancellation takes effect at the end of your current billing
              period
            </li>
            <li>
              You retain access to paid features until the end of the billing
              period
            </li>
            <li>
              You must cancel before 11:59 PM ET the day before your renewal
              date
            </li>
          </ul>

          <h3>8.3 California Residents – 3-Day Cooling-Off Period</h3>
          <div className="highlight-box">
            <p>
              <strong>If you are a California resident</strong>, you have the
              right to cancel your subscription and receive a full refund if no
              more than <strong>three (3) business days</strong> have passed
              since you purchased your subscription (excluding Sundays and
              holidays).
            </p>
            <p>
              <strong>To cancel under California law</strong>, send a signed and
              dated written notice by email to{" "}
              <a href="mailto:support@joinsarv.com">support@joinsarv.com</a> from
              the email address associated with your account.
            </p>
            <p>
              <em>
                Note: We may terminate your access immediately upon receipt of
                your California cancellation notice.
              </em>
            </p>
          </div>

          <h3>8.4 General Refund Policy</h3>
          <div className="warning-box">
            <p>
              <strong>All subscription fees are non-refundable except:</strong>
            </p>
            <ul>
              <li>
                California residents exercising their 3-day right (Section 8.3)
              </li>
              <li>As otherwise required by law</li>
              <li>At our sole discretion on a case-by-case basis</li>
            </ul>
            <p>
              <strong>We do not provide refunds or credits for:</strong>
            </p>
            <ul>
              <li>Partial billing periods</li>
              <li>Unused time on a subscription</li>
              <li>Subscriptions that auto-renew after you fail to cancel</li>
            </ul>
          </div>

          <h3>8.5 Processing Refunds</h3>
          <p>
            Approved refunds will be processed within 10 business days to your
            original payment method.
          </p>
        </section>

        {/* Section 9 */}
        <section id="section9">
          <h2>9. Payment Methods</h2>

          <h3>9.1 How Payments Are Processed</h3>
          <p>
            Subscriptions are currently sold and billed through the Apple App
            Store or Google Play. When you subscribe through an app store, your
            payment is processed by that app store using the payment method
            already on file with your Apple or Google account.
          </p>
          <div className="highlight-box">
            <p>
              <strong>
                For app-store purchases, we do not collect, receive, or store
                your credit card number, bank details, or billing address
              </strong>{" "}
              — all payment details are handled by the app store under its own
              terms and privacy policy. If you subscribe through our website
              instead, your card and billing details are handled by a
              third-party payment processor on our behalf; even then, we do not
              store your full card number on our own systems.
            </p>
          </div>

          <h3>9.2 Authorization to Charge</h3>
          <p>By purchasing a subscription, you:</p>
          <ul>
            <li>
              Authorize the applicable app store to charge the payment method on
              file for all fees
            </li>
            <li>Represent that you are authorized to use that payment method</li>
            <li>Agree to pay all amounts owed, including applicable taxes</li>
          </ul>

          <h3>9.3 Updating Payment Information</h3>
          <p>
            You manage and update your payment method directly in your Apple or
            Google account settings.
          </p>

          <h3>9.4 Payment Disputes</h3>
          <p>
            If you have a question about a charge, contact us first at{" "}
            <a href="mailto:support@joinsarv.com">support@joinsarv.com</a>.
            Refunds and billing disputes are handled according to the applicable
            app store's policies (see Section 10).
          </p>

          <h3>9.5 Taxes</h3>
          <p>
            You are responsible for all applicable sales tax, use tax, VAT, and
            other taxes.
          </p>
        </section>

        {/* Section 10 */}
        <section id="section10">
          <h2>10. Subscriptions Through App Stores (Apple/Google)</h2>

          <h3>10.1 App Store Billing</h3>
          <div className="highlight-box">
            <p>
              If you purchase your subscription through the Apple App Store or
              Google Play Store:
            </p>
            <ul>
              <li>Your subscription is subject to the app store's terms</li>
              <li>The app store processes all payments</li>
              <li>
                Cancellation must be done through the app store (see Section
                8.1)
              </li>
              <li>Refund requests must follow the app store's refund policy</li>
              <li>We do not control app store billing or refunds</li>
            </ul>
          </div>

          <h3>10.2 Apple App Store Terms</h3>
          <p>For subscriptions purchased through Apple's App Store:</p>

          <h4>License Scope</h4>
          <p>You may only use the app:</p>
          <ul>
            <li>On Apple-branded devices running iOS</li>
            <li>As permitted by Apple's "Usage Rules"</li>
            <li>By accounts associated with you via Family Sharing</li>
          </ul>

          <h4>Responsibility</h4>
          <p>
            This Agreement is between you and Sarv only, not Apple. Sarv (not
            Apple) is responsible for the Service.
          </p>

          <h4>Warranty</h4>
          <p>
            If the app fails to conform to any warranty, you may notify Apple
            for a refund. Apple has no other warranty obligation.
          </p>

          <h4>Product Claims</h4>
          <p>Apple is not responsible for:</p>
          <ul>
            <li>Product liability claims</li>
            <li>Claims that the app fails to meet legal requirements</li>
            <li>Claims under consumer protection laws</li>
          </ul>

          <h4>Intellectual Property</h4>
          <p>
            For third-party IP infringement claims, Sarv (not Apple) is
            responsible for investigation and defense.
          </p>

          <h4>Third-Party Beneficiary</h4>
          <p>
            Apple and its subsidiaries are third-party beneficiaries of this
            Agreement as it relates to the iOS app.
          </p>

          <h3>10.3 Google Play Store Terms</h3>
          <p>
            For subscriptions through Google Play, you may have additional
            rights to share the app within your designated family group.
          </p>

          <h3>10.4 Contact for App Store Issues</h3>
          <p>
            Contact Apple or Google directly for issues related to billing,
            refunds, or technical problems with the app stores.
          </p>
        </section>

        {/* Section 11 */}
        <section id="section11">
          <h2>11. User Content and Responsibilities</h2>

          <h3>11.1 Your Content</h3>
          <p>
            "User Content" means any content you post, upload, or transmit
            through the Service, including:
          </p>
          <ul>
            <li>Profile information</li>
            <li>Photos and images</li>
            <li>Comments and reviews</li>
            <li>Messages and communications</li>
            <li>Health data and wellness tracking information</li>
          </ul>

          <h3>11.2 Your Responsibility</h3>
          <p>You are solely responsible for:</p>
          <ul>
            <li>All User Content you post</li>
            <li>Ensuring you have the right to post the content</li>
            <li>Ensuring your content doesn't violate laws or these Terms</li>
            <li>The accuracy of information you provide</li>
          </ul>

          <h3>11.3 License to Your Content</h3>
          <div className="highlight-box">
            <p>
              By posting User Content, you grant Sarv a{" "}
              <strong>
                non-exclusive, worldwide, royalty-free, perpetual, irrevocable,
                sublicensable, and transferable license
              </strong>{" "}
              to:
            </p>
            <ul>
              <li>
                Use, reproduce, modify, adapt, publish, and display your content
              </li>
              <li>Create derivative works from your content</li>
              <li>Distribute and transmit your content</li>
              <li>Use your content for any purpose related to the Service</li>
            </ul>
            <p>This license continues even if you stop using the Service.</p>
          </div>

          <h3>11.4 Content Representations</h3>
          <p>By posting User Content, you represent that:</p>
          <ul>
            <li>You own all rights to the content or have authorization</li>
            <li>Your content doesn't violate anyone's rights</li>
            <li>Your content complies with all laws</li>
            <li>Your content is accurate (especially health information)</li>
          </ul>

          <h3>11.5 No Obligation to Monitor</h3>
          <p>
            We have no obligation to pre-screen or monitor User Content, but we
            reserve the right to review, edit, or remove any content at any
            time.
          </p>

          <h3>11.6 Feedback</h3>
          <p>
            If you provide feedback or suggestions about the Service, we may use
            your feedback without obligation or compensation to you.
          </p>
        </section>

        {/* Section 12 */}
        <section id="section12">
          <h2>12. Prohibited Uses</h2>

          <p>
            <strong>You agree NOT to:</strong>
          </p>

          <h3>12.1 Illegal or Harmful Activities</h3>
          <ul>
            <li>Use the Service for any illegal purpose</li>
            <li>Post harmful, threatening, abusive, or defamatory content</li>
            <li>Impersonate any person or entity</li>
            <li>Engage in any fraudulent activity</li>
          </ul>

          <h3>12.2 Interference with Service</h3>
          <ul>
            <li>Interfere with or disrupt the Service or servers</li>
            <li>Use automated systems (bots, scrapers, crawlers)</li>
            <li>Attempt to gain unauthorized access</li>
            <li>Bypass security measures</li>
            <li>Overload, flood, or spam the Service</li>
          </ul>

          <h3>12.3 Misuse of Information</h3>
          <ul>
            <li>
              Collect personal data about other users without authorization
            </li>
            <li>Use information for commercial purposes without permission</li>
            <li>Sell, transfer, or share accounts or login credentials</li>
          </ul>

          <h3>12.4 Intellectual Property Violations</h3>
          <ul>
            <li>Copy, modify, or create derivative works of the Service</li>
            <li>Reverse engineer or decompile any part of the Service</li>
            <li>Remove or modify copyright or trademark notices</li>
          </ul>

          <h3>12.5 Account Abuse</h3>
          <ul>
            <li>Create multiple accounts to abuse trials or promotions</li>
            <li>Share your account with others</li>
            <li>Use another person's account without permission</li>
            <li>Manipulate reviews or ratings</li>
          </ul>

          <h3>12.6 Healthcare-Specific Prohibitions</h3>
          <ul>
            <li>Provide medical advice or practice medicine</li>
            <li>
              Claim to be a licensed healthcare professional if you are not
            </li>
            <li>Recommend treatments that could be dangerous</li>
            <li>Share misleading health information</li>
          </ul>

          <div className="warning-box">
            <p>
              <strong>
                Violation of these prohibited uses may result in immediate
                termination of your account without refund.
              </strong>
            </p>
          </div>
        </section>

        {/* Section 13 */}
        <section id="section13">
          <h2>13. Intellectual Property Rights</h2>

          <h3>13.1 Sarv's Ownership</h3>
          <p>
            Except for User Content, Sarv owns all rights, title, and interest
            in the Service, including:
          </p>
          <ul>
            <li>The Sarv website and mobile applications</li>
            <li>All software, code, and technology</li>
            <li>All content created by Sarv</li>
            <li>All trademarks, service marks, logos, and branding</li>
            <li>All databases, algorithms, and proprietary methods</li>
          </ul>

          <h3>13.2 Limited License to You</h3>
          <p>
            We grant you a limited, non-exclusive, non-transferable, revocable
            license to access and use the Service for your personal,
            non-commercial use only.
          </p>

          <p>
            <strong>You may NOT:</strong>
          </p>
          <ul>
            <li>
              Reproduce, duplicate, or redistribute any part of the Service
            </li>
            <li>Sell, resell, or commercially exploit the Service</li>
            <li>Modify or create derivative works from the Service</li>
          </ul>

          <h3>13.3 Trademarks</h3>
          <p>
            "Sarv," the Sarv logo, and all related graphics are trademarks of
            M31-V1 LLC. You may not use our trademarks without our express
            written permission.
          </p>

          <h3>13.4 Copyright Protection</h3>
          <p>
            The Service is protected by copyright laws throughout the world. Any
            unauthorized use may violate copyright, trademark, and other laws.
          </p>

          <h3>13.5 DMCA Copyright Infringement Policy</h3>
          <p>
            We respect intellectual property rights. If you believe your
            copyright has been infringed, send written notice to{" "}
            <strong>legal@joinsarv.com</strong> with:
          </p>
          <ol>
            <li>Electronic or physical signature of the copyright owner</li>
            <li>
              Description of the copyrighted work you claim has been infringed
            </li>
            <li>Location of the infringing material (URL or description)</li>
            <li>Your contact information (address, phone, email)</li>
            <li>
              A statement that you have a good faith belief the use is not
              authorized
            </li>
            <li>
              A statement under penalty of perjury that the information is
              accurate
            </li>
          </ol>

          <p>We may terminate accounts of repeat infringers.</p>
        </section>

        {/* Section 14 */}
        <section id="section14">
          <h2>14. Privacy and Data Protection</h2>

          <h3>14.1 Privacy Policy</h3>
          <div className="highlight-box">
            <p>
              Your privacy is important to us. Please review our{" "}
              <strong>
                <a href="/privacy-policy">Privacy Policy</a>
              </strong>{" "}
              for information about:
            </p>
            <ul>
              <li>What data we collect</li>
              <li>How we use your data</li>
              <li>How we protect your data</li>
              <li>Your privacy rights</li>
            </ul>
            <p>
              By using the Service, you consent to the collection and use of
              your data as described in the Privacy Policy.
            </p>
          </div>

          <h3>14.2 HIPAA Compliance</h3>
          <p>
            To the extent we handle Protected Health Information (PHI) as
            defined by HIPAA, we will comply with applicable HIPAA requirements
            to the extent they apply to our Services.
          </p>

          <h3>14.3 International Data Transfers</h3>
          <p>
            If you access the Service from outside the United States, your data
            may be transferred to and processed in the United States.
          </p>

          <h3>14.4 Communications from Sarv</h3>
          <p>By creating an account, you consent to receive:</p>
          <ul>
            <li>Service-related emails and notifications</li>
            <li>Account and billing notifications</li>
            <li>Updates to these Terms or our policies</li>
            <li>Marketing communications (you may opt out)</li>
          </ul>

          <h3>14.5 SMS/Text Messages</h3>
          <p>
            If you opt in, you consent to receive SMS/text messages from Sarv.
            Message and data rates may apply. Text STOP to opt out.
          </p>

          <h3>14.6 Advertising and Social Media</h3>
          <p>
            We use advertising and social media platforms to promote Sarv and
            measure how our ads perform. To do this, we may share limited
            information — such as a hashed email address, device identifiers, and
            activity like app installs or purchases — with these platforms to
            deliver relevant ads and understand their effectiveness. We do not
            share your health data for advertising. For full details and to opt
            out, see our{" "}
            <strong>
              <a href="/privacy-policy">Privacy Policy</a>
            </strong>
            .
          </p>
        </section>

        {/* Section 15 */}
        <section id="section15">
          <h2>15. Third-Party Links and Services</h2>

          <h3>15.1 Third-Party Links</h3>
          <p>
            The Service may contain links to third-party websites or services.
            We do not:
          </p>
          <ul>
            <li>Control third-party websites</li>
            <li>Endorse third-party content, products, or services</li>
            <li>Verify information on third-party sites</li>
            <li>Accept responsibility for third-party sites</li>
          </ul>

          <h3>15.2 Third-Party Service Providers</h3>
          <p>
            We may use third-party service providers for payment processing,
            data analytics, email services, cloud storage, and customer support.
            These providers have their own terms and privacy policies.
          </p>

          <h3>15.3 Healthcare Practitioners</h3>
          <div className="warning-box">
            <p>If the Service provides access to healthcare practitioners:</p>
            <ul>
              <li>
                These practitioners are{" "}
                <strong>independent third parties</strong>, not Sarv employees
              </li>
              <li>
                We do not employ, supervise, or control these practitioners
              </li>
              <li>We do not verify credentials beyond basic screening</li>
              <li>We are not responsible for their conduct or services</li>
              <li>Your relationship is directly with the practitioner</li>
            </ul>
          </div>

          <h3>15.4 Your Use of Third-Party Services</h3>
          <p>
            When you access third-party services, you do so at your own risk and
            are subject to their terms and policies.
          </p>
        </section>

        {/* Section 16 */}
        <section id="section16">
          <h2>16. Service Modifications and Availability</h2>

          <h3>16.1 Changes to the Service</h3>
          <p>We reserve the right to:</p>
          <ul>
            <li>Modify, update, or discontinue any feature</li>
            <li>Change pricing, subscription plans, or features</li>
            <li>Impose limits on certain features</li>
            <li>Restrict access to parts of the Service</li>
          </ul>
          <p>
            We may make changes with or without notice and without liability to
            you.
          </p>

          <h3>16.2 No Guarantee of Availability</h3>
          <p>We do not guarantee that the Service will:</p>
          <ul>
            <li>Be available at all times without interruption</li>
            <li>Be error-free or secure</li>
            <li>Meet your specific requirements</li>
            <li>Provide accurate or complete results</li>
          </ul>

          <h3>16.3 Maintenance and Downtime</h3>
          <p>
            The Service may be unavailable due to scheduled maintenance,
            technical issues, or circumstances beyond our control. We are not
            liable for any unavailability.
          </p>

          <h3>16.4 Beta Features</h3>
          <p>
            We may offer beta or experimental features. These features are
            provided "as is" without warranties and may be discontinued at any
            time.
          </p>
        </section>

        {/* Section 17 */}
        <section id="section17">
          <h2>17. Termination of Your Account</h2>

          <h3>17.1 Termination by You</h3>
          <p>You may terminate your account at any time by:</p>
          <ul>
            <li>Canceling your subscription (see Section 8)</li>
            <li>Requesting account deletion through Account Settings</li>
            <li>Emailing us at support@joinsarv.com</li>
          </ul>

          <h3>17.2 Termination by Us</h3>
          <p>
            We reserve the right to suspend or terminate your account
            immediately if:
          </p>
          <ul>
            <li>You violate these Terms</li>
            <li>You engage in fraudulent or illegal activity</li>
            <li>Your payment fails</li>
            <li>You abuse or misuse the Service</li>
            <li>We are required to do so by law</li>
          </ul>

          <h3>17.3 Effect of Termination</h3>
          <p>When your account is terminated:</p>
          <ul>
            <li>Your right to access the Service ends immediately</li>
            <li>We may delete your data and User Content</li>
            <li>
              You remain liable for all charges incurred before termination
            </li>
            <li>No refunds will be provided (except as required by law)</li>
          </ul>

          <h3>17.4 Provisions That Survive</h3>
          <p>
            The following sections survive termination: Sections 4 (Medical
            Disclaimers), 11.3 (License to Content), 13 (Intellectual Property),
            18 (Disclaimers), 19 (Limitation of Liability), 20
            (Indemnification), 21-23 (Dispute Resolution)
          </p>
        </section>

        {/* Section 18 */}
        <section id="section18">
          <h2>18. Disclaimers and Limitations of Liability</h2>

          <h3>18.1 "AS IS" and "AS AVAILABLE"</h3>
          <div className="warning-box">
            <p className="caps-text">
              <strong>
                TO THE MAXIMUM EXTENT PERMITTED BY LAW, THE SERVICE IS PROVIDED
                "AS IS" AND "AS AVAILABLE" WITHOUT ANY WARRANTIES OF ANY KIND,
                WHETHER EXPRESS, IMPLIED, OR STATUTORY.
              </strong>
            </p>

            <p>
              <strong>We expressly disclaim all warranties, including:</strong>
            </p>
            <ul>
              <li>
                Implied warranties of merchantability and fitness for a
                particular purpose
              </li>
              <li>
                Warranties that the Service will be uninterrupted or error-free
              </li>
              <li>Warranties that information is accurate or complete</li>
              <li>Warranties that defects will be corrected</li>
              <li>Warranties that the Service is free from viruses</li>
            </ul>
          </div>

          <h3>18.2 Health and Medical Disclaimers</h3>
          <div className="warning-box">
            <p className="caps-text">
              <strong>WE MAKE NO WARRANTIES REGARDING:</strong>
            </p>
            <ul>
              <li>The accuracy or reliability of any health information</li>
              <li>Results you may achieve using the Service</li>
              <li>
                The safety or effectiveness of any herbs or recommendations
              </li>
              <li>The qualifications or conduct of healthcare practitioners</li>
            </ul>
            <p className="caps-text">
              <strong>
                YOUR USE OF ANY INFORMATION OR RECOMMENDATIONS IS AT YOUR OWN
                RISK.
              </strong>
            </p>
          </div>

          <h3>18.3 User Content Disclaimer</h3>
          <p>
            <strong>
              We do not verify, endorse, or take responsibility for any User
              Content or third-party content.
            </strong>{" "}
            You rely on such content at your own risk.
          </p>

          <h3>18.4 LIMITATION OF LIABILITY</h3>
          <div className="warning-box critical">
            <p className="caps-text">
              <strong>TO THE MAXIMUM EXTENT PERMITTED BY LAW:</strong>
            </p>

            <h4>A) Excluded Damages</h4>
            <p className="caps-text">
              <strong>
                WE ARE NOT LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL,
                CONSEQUENTIAL, PUNITIVE, OR EXEMPLARY DAMAGES, INCLUDING:
              </strong>
            </p>
            <ul>
              <li>Loss of profits, revenue, or business</li>
              <li>Loss of data or information</li>
              <li>Loss of goodwill or reputation</li>
              <li>Cost of substitute services</li>
              <li>Personal injury or property damage</li>
              <li>Emotional distress</li>
            </ul>

            <h4>B) Cap on Liability</h4>
            <p className="caps-text">
              <strong>
                OUR TOTAL AGGREGATE LIABILITY TO YOU FOR ALL CLAIMS IS LIMITED
                TO THE GREATER OF:
              </strong>
            </p>
            <ol>
              <li>
                The total amount you paid us in the 12 months before the event,
                OR
              </li>
              <li>$100.00</li>
            </ol>
          </div>

          <h3>18.5 Jurisdictional Limitations</h3>
          <p>
            Some jurisdictions do not allow certain exclusions or limitations.
            If these laws apply to you, some limitations may not apply and you
            may have additional rights.
          </p>

          <h3>18.6 Release of Claims</h3>
          <div className="warning-box">
            <p className="caps-text">
              <strong>
                TO THE MAXIMUM EXTENT PERMITTED BY LAW, YOU RELEASE AND WAIVE
                ANY AND ALL CLAIMS AGAINST SARV ARISING FROM:
              </strong>
            </p>
            <ul>
              <li>Your use of the Service</li>
              <li>The conduct of other users</li>
              <li>Content posted by others</li>
              <li>Services provided by third parties</li>
            </ul>
          </div>
        </section>

        {/* Section 19 */}
        <section id="section19">
          <h2>19. Indemnification</h2>

          <h3>19.1 Your Indemnification Obligation</h3>
          <p>
            You agree to defend, indemnify, and hold harmless Sarv and our
            affiliates, directors, officers, employees, and agents from any
            claims, damages, losses, liabilities, and expenses (including
            reasonable attorneys' fees) arising from:
          </p>
          <ul>
            <li>Your access to or use of the Service</li>
            <li>Your violation of these Terms</li>
            <li>Your violation of any law or regulation</li>
            <li>Your violation of any third-party rights</li>
            <li>Your User Content</li>
            <li>Your negligence or willful misconduct</li>
            <li>Any false or misleading information you provide</li>
          </ul>

          <h3>19.2 Defense of Claims</h3>
          <p>
            We reserve the right to assume the exclusive defense of any matter
            subject to indemnification by you, in which case you will cooperate
            with us.
          </p>

          <h3>19.3 Survival</h3>
          <p>
            This indemnification obligation survives termination of your account
            and these Terms.
          </p>
        </section>

        {/* Section 20 */}
        <section id="section20">
          <h2>20. Export Control and Sanctions</h2>

          <h3>20.1 Export Restrictions</h3>
          <p>
            The Service and technology may be subject to U.S. export control
            laws. You agree that you will not:
          </p>
          <ul>
            <li>Export or re-export the Service to any prohibited country</li>
            <li>Use the Service in violation of U.S. export laws</li>
            <li>
              Provide access to persons on U.S. government restricted parties
              lists
            </li>
          </ul>

          <h3>20.2 Your Representations</h3>
          <p>You represent and warrant that:</p>
          <ul>
            <li>You are not located in a country subject to U.S. embargo</li>
            <li>You are not on any U.S. government prohibited parties list</li>
            <li>You will comply with all export control and sanctions laws</li>
          </ul>

          <h3>20.3 Sanctions Compliance</h3>
          <p>
            You may not use the Service if you are subject to U.S. or other
            applicable sanctions. We may terminate your access if we determine
            you are in violation.
          </p>
        </section>

        {/* Section 21 */}
        <section id="section21">
          <h2>21. Dispute Resolution and Arbitration Agreement</h2>

          <div className="warning-box critical">
            <p className="caps-text">
              <strong>
                PLEASE READ THIS SECTION CAREFULLY. IT AFFECTS YOUR LEGAL
                RIGHTS, INCLUDING YOUR RIGHT TO FILE A LAWSUIT IN COURT AND TO
                HAVE A JURY TRIAL.
              </strong>
            </p>
          </div>

          <h3>21.1 Mandatory Arbitration</h3>
          <div className="highlight-box">
            <p>
              <strong>
                You and Sarv agree that any dispute arising out of or relating
                to these Terms or the Service will be resolved by binding
                individual arbitration, rather than in court
              </strong>
              , except:
            </p>
            <ul>
              <li>Claims that qualify for small claims court</li>
              <li>
                Either party may seek injunctive relief in court for IP, data
                security, or unauthorized access matters
              </li>
            </ul>
          </div>

          <h3>21.2 INFORMAL DISPUTE RESOLUTION FIRST (REQUIRED)</h3>
          <div className="warning-box">
            <p>
              <strong>
                Before filing an arbitration, you and we must first try to
                resolve the dispute informally:
              </strong>
            </p>

            <h4>a) Notice Requirement</h4>
            <p>
              The party raising the dispute must send a written "Notice of
              Dispute" that includes:
            </p>
            <ul>
              <li>Your name and email address</li>
              <li>A detailed description of the dispute</li>
              <li>The relief requested and proposed resolution</li>
            </ul>

            <p>
              <strong>Send Notice to Sarv at:</strong>
            </p>
            <p style={{ marginLeft: "20px" }}>
              Email:{" "}
              <a href="mailto:support@joinsarv.com">support@joinsarv.com</a>
            </p>

            <h4>b) Settlement Conference</h4>
            <p>
              After receiving the Notice, parties have <strong>60 days</strong>{" "}
              to investigate and attempt to resolve the dispute. Either party
              may request a settlement conference.
            </p>

            <h4>c) Prerequisite to Arbitration</h4>
            <p className="caps-text">
              <strong>
                YOU CANNOT FILE AN ARBITRATION UNTIL THE 60-DAY INFORMAL DISPUTE
                RESOLUTION PERIOD HAS ENDED.
              </strong>
            </p>
          </div>

          <h3>21.3 Arbitration Procedure</h3>

          <h4>a) Administrator</h4>
          <p>
            Any arbitration will be administered by{" "}
            <strong>National Arbitration and Mediation (NAM)</strong> under its
            Comprehensive Dispute Resolution Rules.
          </p>

          <h4>b) Initiating Arbitration</h4>
          <p>
            To initiate arbitration (after completing informal resolution), file
            a demand with NAM and send a copy to{" "}
            <a href="mailto:legal@joinsarv.com">legal@joinsarv.com</a>.
          </p>

          <h4>c) Location</h4>
          <p>
            Unless we agree otherwise, arbitration will be conducted in the
            county where you reside.
          </p>

          <h4>d) Arbitrator</h4>
          <p>
            The arbitrator will be a neutral party selected according to NAM
            rules. The arbitrator must follow applicable law.
          </p>

          <h4>e) Fees</h4>
          <p>
            Each party is responsible for NAM filing, administrative, and
            arbitrator fees in accordance with NAM Rules.
          </p>

          <h4>f) Decision</h4>
          <p>
            The arbitrator must issue a written decision. The decision is final
            and binding. A court may enter judgment on the award.
          </p>

          <h3>21.4 ADDITIONAL PROCEDURES FOR MASS FILINGS</h3>
          <p>
            If <strong>25 or more similar claims</strong> are filed by the same
            or coordinated counsel ("Mass Filing"), a staged process applies:
          </p>

          <h4>Stage One:</h4>
          <ul>
            <li>Counsel for each side selects 25 claims to proceed</li>
            <li>All other claims are held in abeyance (no fees owed)</li>
            <li>
              After completion, parties engage in global mediation (Sarv pays
              fee)
            </li>
          </ul>

          <h4>Stage Two:</h4>
          <ul>
            <li>If not resolved, counsel select 50 additional claims</li>
            <li>Same process with second global mediation</li>
          </ul>

          <h4>After Stage Two:</h4>
          <ul>
            <li>
              Any remaining unresolved claims may proceed in court (not
              arbitration)
            </li>
          </ul>

          <h3>21.5 CLASS ACTION WAIVER (See Section 22)</h3>

          <h3>21.6 OPT-OUT RIGHT</h3>
          <div className="highlight-box">
            <p>
              <strong>
                You have the right to opt out of this Arbitration Agreement:
              </strong>
            </p>

            <h4>Deadline:</h4>
            <p>
              You must opt out within <strong>30 days</strong> after you first
              accept these Terms.
            </p>

            <h4>How to Opt Out:</h4>
            <p>
              Email a signed written notice stating that you opt out of the
              Arbitration Agreement to{" "}
              <a href="mailto:legal@joinsarv.com">legal@joinsarv.com</a> from the
              email address associated with your account.
            </p>

            <h4>Required Information:</h4>
            <ul>
              <li>Your full name</li>
              <li>Your email address for your Sarv account</li>
              <li>A clear statement that you opt out</li>
              <li>Your signature</li>
            </ul>

            <p className="caps-text">
              <strong>
                THIS IS THE ONLY WAY TO OPT OUT. IF YOU DO NOT OPT OUT WITHIN 30
                DAYS, YOU WILL BE BOUND BY THE ARBITRATION AGREEMENT.
              </strong>
            </p>
          </div>

          <h3>21.7 Severability</h3>
          <p>
            If any part of this Arbitration Agreement is found invalid, that
            part will be severed and the remainder will remain in effect (except
            that if the Class Action Waiver is found invalid, the entire
            Arbitration Agreement is void).
          </p>
        </section>

        {/* Section 22 */}
        <section id="section22">
          <h2>22. Class Action Waiver and Jury Trial Waiver</h2>

          <h3>22.1 CLASS ACTION WAIVER</h3>
          <div className="warning-box critical">
            <p className="caps-text">
              <strong>
                TO THE FULLEST EXTENT PERMITTED BY LAW, YOU AND SARV AGREE THAT:
              </strong>
            </p>

            <p className="caps-text">
              <strong>
                ALL DISPUTES WILL BE RESOLVED ON AN INDIVIDUAL BASIS ONLY. YOU
                AND SARV WAIVE THE RIGHT TO BRING OR PARTICIPATE IN:
              </strong>
            </p>
            <ul>
              <li>Class actions</li>
              <li>Class arbitrations</li>
              <li>Collective actions</li>
              <li>Representative actions</li>
              <li>Private attorney general actions</li>
            </ul>

            <p>
              The arbitrator may not consolidate more than one person's claims
              and may not preside over any class or representative proceeding.
            </p>
          </div>

          <h3>22.2 JURY TRIAL WAIVER</h3>
          <div className="warning-box critical">
            <p className="caps-text">
              <strong>
                TO THE FULLEST EXTENT PERMITTED BY LAW, YOU AND SARV WAIVE ANY
                RIGHT TO A JURY TRIAL IN ANY LEGAL PROCEEDING ARISING OUT OF OR
                RELATED TO THESE TERMS OR THE SERVICE.
              </strong>
            </p>
          </div>

          <h3>22.3 Right to Class Settlement</h3>
          <p>
            Notwithstanding the Class Action Waiver, the parties retain the
            right to participate in a class-wide settlement.
          </p>

          <h3>22.4 Severability</h3>
          <p>
            If the Class Action Waiver is found unenforceable, then the entire
            Arbitration Agreement is void, but all other Terms remain in effect.
          </p>
        </section>

        {/* Section 23 */}
        <section id="section23">
          <h2>23. Governing Law and Jurisdiction</h2>

          <h3>23.1 Governing Law</h3>
          <p>
            These Terms are governed by the{" "}
            <strong>laws of the State of Delaware</strong>, without regard to
            conflict of law principles.
          </p>

          <h3>23.2 Exclusive Jurisdiction</h3>
          <p>
            For disputes not subject to arbitration (because you opted out or
            the dispute is not arbitrable):
          </p>
          <ul>
            <li>
              <strong>Exclusive Venue:</strong> State and federal courts located
              in Delaware
            </li>
            <li>
              <strong>Consent to Jurisdiction:</strong> You consent to the
              jurisdiction of these courts
            </li>
          </ul>

          <h3>23.3 Statute of Limitations</h3>
          <div className="warning-box">
            <p>
              <strong>
                You must bring any claim within ONE (1) YEAR after it arises,
                regardless of any statute or law to the contrary. After one
                year, the claim is permanently barred.
              </strong>
            </p>
          </div>

          <h3>23.4 United Nations Convention</h3>
          <p>
            The United Nations Convention on Contracts for the International
            Sale of Goods does not apply to these Terms.
          </p>
        </section>

        {/* Section 24 */}
        <section id="section24">
          <h2>24. General Legal Terms</h2>

          <h3>24.1 Entire Agreement</h3>
          <p>
            These Terms, together with our Privacy Policy, HIPAA Notice, and
            Community Guidelines, constitute the entire agreement between you and
            Sarv.
          </p>

          <h3>24.2 Modifications to Terms</h3>
          <p>
            We reserve the right to modify these Terms at any time. When we make
            material changes, we will notify you. Your continued use constitutes
            acceptance.
          </p>

          <h3>24.3 Severability</h3>
          <p>
            If any provision is found invalid, it will be modified to the
            minimum extent necessary or severed. The remaining provisions remain
            in effect.
          </p>

          <h3>24.4 Waiver</h3>
          <p>
            Our failure to enforce any provision does not constitute a waiver of
            that provision or our right to enforce it later.
          </p>

          <h3>24.5 Assignment</h3>
          <p>
            You may not transfer or assign your account or these Terms without
            our consent. We may assign these Terms to any third party without
            your consent.
          </p>

          <h3>24.6 Force Majeure</h3>
          <p>
            We are not liable for any delay or failure to perform due to causes
            beyond our reasonable control (acts of God, war, government actions,
            etc.).
          </p>

          <h3>24.7 No Third-Party Beneficiaries</h3>
          <p>
            These Terms are for the benefit of you and Sarv only (except as
            expressly stated, such as app store beneficiaries).
          </p>

          <h3>24.8 Notices</h3>
          <p>
            <strong>To You:</strong> We may provide notices by email, posting on
            the Service, push notification, or mail.
          </p>
          <p>
            <strong>To Us:</strong> Send notices to{" "}
            <a href="mailto:legal@joinsarv.com">legal@joinsarv.com</a>.
          </p>

          <h3>24.9 Language</h3>
          <p>
            These Terms are written in English. Any translations are for
            convenience only. In case of conflicts, the English version
            prevails.
          </p>

          <h3>24.10 Electronic Signatures</h3>
          <p>
            By clicking "I Agree" or creating an account, you acknowledge that
            your electronic acceptance has the same legal effect as a
            handwritten signature.
          </p>

          <h3>24.11 California Residents</h3>
          <div className="highlight-box">
            <p>California users may report complaints to:</p>
            <p style={{ marginLeft: "20px" }}>
              <strong>Complaint Assistance Unit</strong>
              <br />
              Division of Consumer Services
              <br />
              California Department of Consumer Affairs
              <br />
              1625 North Market Blvd., Suite N 112
              <br />
              Sacramento, CA 95834
              <br />
              Phone: (800) 952-5210
            </p>
          </div>
        </section>

        {/* Section 25 */}
        <section id="section25">
          <h2>25. How to Contact Us</h2>

          <div className="contact-box">
            <h3>Contact Information</h3>

            <p>
              <strong>M31-V1 LLC</strong>
            </p>
            <p>
              <strong>Email:</strong> support@joinsarv.com
            </p>
            <p>
              <strong>Legal Inquiries:</strong> legal@joinsarv.com
            </p>

            <h4 style={{ marginTop: "20px" }}>Specific Contacts:</h4>
            <ul>
              <li>
                <strong>Copyright/DMCA:</strong> legal@joinsarv.com (subject: "DMCA
                Notice")
              </li>
              <li>
                <strong>Privacy Inquiries:</strong> privacy@joinsarv.com
              </li>
              <li>
                <strong>Arbitration Opt-Out:</strong> legal@joinsarv.com
                (subject: "Arbitration Opt-Out")
              </li>
            </ul>
          </div>
        </section>

        {/* Acknowledgment */}
        <div className="acknowledgment-box">
          <h2>ACKNOWLEDGMENT</h2>
          <p className="caps-text">
            <strong>
              BY CLICKING "I AGREE," CREATING AN ACCOUNT, OR USING THE SERVICE,
              YOU ACKNOWLEDGE THAT YOU HAVE READ, UNDERSTOOD, AND AGREE TO BE
              BOUND BY THESE TERMS OF USE.
            </strong>
          </p>

          <p className="caps-text">
            <strong>
              YOU ALSO ACKNOWLEDGE THAT YOU HAVE READ AND AGREE TO OUR PRIVACY
              POLICY AND CONSENT TO THE COLLECTION, USE, AND DISCLOSURE OF YOUR
              INFORMATION AS DESCRIBED THEREIN.
            </strong>
          </p>

          <p className="caps-text">
            <strong>
              IF YOU DO NOT AGREE TO THESE TERMS, DO NOT USE THE SERVICE.
            </strong>
          </p>
        </div>

        <div className="footer-section">
          <p>
            <strong>Last Updated: June 5, 2026</strong>
          </p>
          <p>© 2026 M31-V1 LLC. All Rights Reserved.</p>
        </div>
      </Container>
    </div>
  );
};

export default TermsOfService;
